import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';

import { AGENDA_ITEM_MAX_DURATION_MINUTES } from '@/components/MeetingAgendaBuilder/agendaLimits';

/** Mirrors MeetingPlanTemplateLimits on the server: a value it refuses must never reach it. */
export const TITLE_MAX_LENGTH = 200;
export const OBJECTIVE_MAX_LENGTH = 1000;
export const OPENING_STATEMENT_TITLE_MAX_LENGTH = 200;
export const OPENING_STATEMENT_TEXT_MAX_LENGTH = 2000;
export const AGENDA_ITEM_TITLE_MAX_LENGTH = 200;
export const AGENDA_ITEM_DESCRIPTION_MAX_LENGTH = 2000;
export const AGENDA_ITEMS_MAX_COUNT = 20;
export const INVITATION_TEXT_MAX_LENGTH = 4000;
export const LIST_ENTRY_MAX_LENGTH = 2000;
export const TIP_TITLE_MAX_LENGTH = 200;
export const LIST_MAX_ENTRIES = 20;

const TITLE_REQUIRED_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.titleRequired';
const TITLE_TOO_LONG_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.titleTooLong';
const OBJECTIVE_REQUIRED_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.objectiveRequired';
const OPENING_TEXT_REQUIRED_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.openingStatementTextRequired';
const TEXT_TOO_LONG_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.textTooLong';
const AGENDA_ITEM_TITLE_REQUIRED_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.agendaItemTitleRequired';
const AGENDA_ITEMS_TOO_MANY_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.agendaItemsTooMany';
const DURATION_OUT_OF_RANGE_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.durationOutOfRange';
const LIST_ENTRY_REQUIRED_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.listEntryRequired';
const LIST_TOO_MANY_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.listTooMany';
const TIP_TITLE_REQUIRED_MESSAGE: TranslationKey =
    'templateManagement.meetingPlanEditor.validation.tipTitleRequired';

export const MeetingPlanTemplateFormFields = {
    isActive: 'isActive',
    title: 'title',
    objective: 'objective',
    invitationText: 'invitationText',
    openingStatementTitle: 'openingStatementTitle',
    openingStatementText: 'openingStatementText',
    agendaItems: 'agendaItems',
    preparationQuestions: 'preparationQuestions',
    tips: 'tips',
    observations: 'observations',
    conversationTips: 'conversationTips',
} as const;

const localizedTextSchema = z.record(z.string(), z.string());

/**
 * The bilingual wording an agenda subject already carried, kept beside the one language being
 * retyped so a reorder, an insertion or a deletion never hands another subject's translation to it.
 * Null for a subject the editor has just added: it has no other language yet.
 */
const storedAgendaItemTextSchema = z
    .object({ title: localizedTextSchema, description: localizedTextSchema })
    .nullable();

/**
 * One entry of a plan's plain text lists. `storedText` carries the wording the entry already had in
 * the languages nobody is retyping, for the same reason an agenda subject does: the entries have no
 * identifier, so a reorder must not hand one entry another's translation.
 */
const textListEntrySchema = z.object({
    text: z
        .string()
        .trim()
        .min(1, { message: LIST_ENTRY_REQUIRED_MESSAGE })
        .max(LIST_ENTRY_MAX_LENGTH, { message: TEXT_TOO_LONG_MESSAGE }),
    storedText: localizedTextSchema.nullable(),
});

const conversationTipSchema = z.object({
    title: z
        .string()
        .trim()
        .min(1, { message: TIP_TITLE_REQUIRED_MESSAGE })
        .max(TIP_TITLE_MAX_LENGTH, { message: TITLE_TOO_LONG_MESSAGE }),
    description: z.string().trim().max(LIST_ENTRY_MAX_LENGTH, { message: TEXT_TOO_LONG_MESSAGE }),
    storedText: z
        .object({ title: localizedTextSchema, description: localizedTextSchema })
        .nullable(),
});

const textList = z.array(textListEntrySchema).max(LIST_MAX_ENTRIES, {
    message: LIST_TOO_MANY_MESSAGE,
});

const agendaItemSchema = z.object({
    title: z
        .string()
        .trim()
        .min(1, { message: AGENDA_ITEM_TITLE_REQUIRED_MESSAGE })
        .max(AGENDA_ITEM_TITLE_MAX_LENGTH, { message: TITLE_TOO_LONG_MESSAGE }),
    // The message is declared on the type as well, so a field left blank is refused in the
    // reader's language: without it `zod` hands back its own English sentence, which the form
    // would then read as a translation key and show raw.
    durationMinutes: z
        .number({ message: DURATION_OUT_OF_RANGE_MESSAGE })
        .int({ message: DURATION_OUT_OF_RANGE_MESSAGE })
        .min(0, { message: DURATION_OUT_OF_RANGE_MESSAGE })
        .max(AGENDA_ITEM_MAX_DURATION_MINUTES, { message: DURATION_OUT_OF_RANGE_MESSAGE }),
    description: z.string().trim().max(AGENDA_ITEM_DESCRIPTION_MAX_LENGTH, {
        message: TEXT_TOO_LONG_MESSAGE,
    }),
    storedText: storedAgendaItemTextSchema,
    // Bilingual already: the shared agenda editor bounds their number and their length.
    discussionPoints: z.array(
        z.object({ title: localizedTextSchema, context: localizedTextSchema }),
    ),
});

export const meetingPlanTemplateSchema = z.object({
    [MeetingPlanTemplateFormFields.isActive]: z.boolean(),
    [MeetingPlanTemplateFormFields.title]: z
        .string()
        .trim()
        .min(1, { message: TITLE_REQUIRED_MESSAGE })
        .max(TITLE_MAX_LENGTH, { message: TITLE_TOO_LONG_MESSAGE }),
    [MeetingPlanTemplateFormFields.openingStatementTitle]: z
        .string()
        .trim()
        .max(OPENING_STATEMENT_TITLE_MAX_LENGTH, { message: TITLE_TOO_LONG_MESSAGE }),
    [MeetingPlanTemplateFormFields.openingStatementText]: z
        .string()
        .trim()
        .min(1, { message: OPENING_TEXT_REQUIRED_MESSAGE })
        .max(OPENING_STATEMENT_TEXT_MAX_LENGTH, { message: TEXT_TOO_LONG_MESSAGE }),
    [MeetingPlanTemplateFormFields.agendaItems]: z
        .array(agendaItemSchema)
        .max(AGENDA_ITEMS_MAX_COUNT, { message: AGENDA_ITEMS_TOO_MANY_MESSAGE }),
    [MeetingPlanTemplateFormFields.objective]: z
        .string()
        .trim()
        .min(1, { message: OBJECTIVE_REQUIRED_MESSAGE })
        .max(OBJECTIVE_MAX_LENGTH, { message: TEXT_TOO_LONG_MESSAGE }),
    [MeetingPlanTemplateFormFields.invitationText]: z
        .string()
        .trim()
        .max(INVITATION_TEXT_MAX_LENGTH, { message: TEXT_TOO_LONG_MESSAGE }),
    [MeetingPlanTemplateFormFields.preparationQuestions]: textList,
    [MeetingPlanTemplateFormFields.tips]: textList,
    [MeetingPlanTemplateFormFields.observations]: textList,
    [MeetingPlanTemplateFormFields.conversationTips]: z
        .array(conversationTipSchema)
        .max(LIST_MAX_ENTRIES, { message: LIST_TOO_MANY_MESSAGE }),
});

export type MeetingPlanTemplateFormData = z.infer<typeof meetingPlanTemplateSchema>;
export type MeetingPlanAgendaItemFormData = z.infer<typeof agendaItemSchema>;
export type MeetingPlanTextListEntryFormData = z.infer<typeof textListEntrySchema>;
export type MeetingPlanConversationTipFormData = z.infer<typeof conversationTipSchema>;

/** The three plain text lists, named once so the form and its sections cannot disagree. */
export const TEXT_LIST_FIELDS = [
    MeetingPlanTemplateFormFields.preparationQuestions,
    MeetingPlanTemplateFormFields.tips,
    MeetingPlanTemplateFormFields.observations,
] as const;

export type MeetingPlanTextListField = (typeof TEXT_LIST_FIELDS)[number];
